import { GitBranch, Plus } from "lucide-react";
import { ResizablePanel } from "../ui/resizable";
import { ScrollArea } from "../ui/scroll-area";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import type { Id } from "@/convex/src/_generated/dataModel";

type DetailTab = "overview" | "nodes" | "edges" | "drivers";

interface FlowNode {
  _id: Id<"flowInteractionModules">;
  flowId: Id<"flows">;
  interactionModuleId: Id<"interactionModules">;
  label?: string;
  positionX?: number;
  positionY?: number;
}

interface FlowDriver {
  _id: Id<"flowDrivers">;
  type: string;
  name?: string;
  description?: string;
  config?: unknown;
  edgeId: Id<"flowEdges">;
}

interface FlowEdge {
  _id: Id<"flowEdges">;
  fromNodeId: Id<"flowInteractionModules">;
  toNodeId?: Id<"flowInteractionModules">;
  conditionKind?: string;
  conditionConfig?: unknown;
  priority: number;
  driver: FlowDriver | null;
}

interface InteractionModule {
  _id: Id<"interactionModules">;
  name: string;
  slug: string;
  description?: string;
}

interface Flow {
  _id: Id<"flows">;
  name: string;
  slug: string;
  description?: string;
  status: string;
  entryNodeId?: Id<"flowInteractionModules">;
  createdAt: number;
  updatedAt: number;
}

interface FlowGraphData {
  flow: Flow;
  nodes: FlowNode[];
  edges: FlowEdge[];
  interactionModules: InteractionModule[];
}

interface FlowDetailPanelProps {
  flowGraph: FlowGraphData | null | undefined;
  isLoading: boolean;
  detailTab: DetailTab;
  onDetailTabChange: (tab: DetailTab) => void;
  onAddStep?: () => void;
  onAddEdge?: () => void;
}

function getStatusVariant(
  status: string,
): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case "active":
      return "default";
    case "draft":
      return "secondary";
    case "archived":
      return "outline";
    default:
      return "secondary";
  }
}

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleString();
}

export function FlowDetailPanel({
  flowGraph,
  isLoading,
  detailTab,
  onDetailTabChange,
  onAddStep,
  onAddEdge,
}: FlowDetailPanelProps) {
  if (!flowGraph) {
    return (
      <ResizablePanel defaultSize={65} minSize={30}>
        <div className="h-full flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
              <GitBranch className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="text-xs font-semibold text-foreground mb-1">
              {isLoading ? "Loading flow..." : "Select a flow"}
            </p>
            <p className="text-xs text-muted-foreground font-medium">
              {isLoading
                ? "Fetching flow details"
                : "Choose a flow from the left to inspect its details"}
            </p>
          </div>
        </div>
      </ResizablePanel>
    );
  }

  const { flow, nodes, edges, interactionModules } = flowGraph;

  // Build lookup maps
  const moduleMap = new Map(interactionModules.map((m) => [m._id, m]));
  const nodeMap = new Map(nodes.map((n) => [n._id, n]));

  // Get node label with fallback to module name
  const getNodeLabel = (
    nodeId: Id<"flowInteractionModules"> | undefined,
  ): string => {
    if (!nodeId) return "(End)";
    const node = nodeMap.get(nodeId);
    if (!node) return "(Unknown)";
    if (node.label) return node.label;
    const mod = moduleMap.get(node.interactionModuleId);
    return mod?.name || "(Unknown Module)";
  };

  // Get entry node label
  const entryNodeLabel = flow.entryNodeId
    ? getNodeLabel(flow.entryNodeId)
    : "(Not set)";

  // Collect all drivers from edges
  const drivers = edges.filter((e) => e.driver).map((e) => e.driver!);

  const tabs: { key: DetailTab; label: string }[] = [
    { key: "overview", label: "Overview" },
    { key: "nodes", label: `Steps (${nodes.length})` },
    { key: "edges", label: `Edges (${edges.length})` },
    { key: "drivers", label: `Drivers (${drivers.length})` },
  ];

  return (
    <ResizablePanel defaultSize={65} minSize={30}>
      <div className="h-full flex flex-col">
        {/* Header */}
        <div className="border-b border-border px-6 py-4">
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-lg font-semibold">{flow.name}</h2>
            <Badge variant={getStatusVariant(flow.status)} className="text-xs">
              {flow.status}
            </Badge>
          </div>
          <code className="text-xs text-muted-foreground font-mono">
            {flow.slug}
          </code>
        </div>

        {/* Tab Strip */}
        <div className="border-b border-border px-6">
          <div className="flex gap-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                role="tab"
                aria-selected={detailTab === tab.key}
                onClick={() => onDetailTabChange(tab.key)}
                className={`px-3 py-2 text-xs font-medium rounded-t-md transition-all ${
                  detailTab === tab.key
                    ? "bg-background text-foreground border-b-2 border-primary"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-hidden">
          <ScrollArea className="h-full">
            <div className="p-6">
              {/* Overview Tab */}
              {detailTab === "overview" && (
                <div className="space-y-4">
                  {/* Empty state for flows with no steps */}
                  {nodes.length === 0 && (
                    <div className="p-4 bg-muted/30 border border-border rounded-lg mb-6">
                      <div className="flex flex-col items-center text-center py-4">
                        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mb-3">
                          <GitBranch className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <p className="text-sm font-medium text-foreground mb-1">
                          This flow has no steps yet
                        </p>
                        <p className="text-xs text-muted-foreground mb-4">
                          Add steps to define the journey through this flow.
                        </p>
                        {onAddStep && (
                          <Button onClick={onAddStep} size="sm">
                            <Plus className="h-3 w-3 mr-2" />
                            Add step
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <label className="text-xs font-medium text-muted-foreground">
                      Name
                    </label>
                    <p className="text-sm mt-1">{flow.name}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">
                      Slug
                    </label>
                    <p className="text-sm mt-1 font-mono">{flow.slug}</p>
                  </div>
                  {flow.description && (
                    <div>
                      <label className="text-xs font-medium text-muted-foreground">
                        Description
                      </label>
                      <p className="text-sm mt-1">{flow.description}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">
                      Status
                    </label>
                    <p className="text-sm mt-1">
                      <Badge variant={getStatusVariant(flow.status)}>
                        {flow.status}
                      </Badge>
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">
                      Entry Step
                    </label>
                    <p className="text-sm mt-1">{entryNodeLabel}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">
                      Created
                    </label>
                    <p className="text-sm mt-1 text-muted-foreground">
                      {formatDate(flow.createdAt)}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">
                      Updated
                    </label>
                    <p className="text-sm mt-1 text-muted-foreground">
                      {formatDate(flow.updatedAt)}
                    </p>
                  </div>
                </div>
              )}

              {/* Steps Tab */}
              {detailTab === "nodes" && (
                <div className="space-y-2">
                  {nodes.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No steps in this flow.
                    </p>
                  ) : (
                    <div className="border border-border rounded-md overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">
                              Step
                            </th>
                            <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">
                              Interaction
                            </th>
                            <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">
                              Position
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {nodes.map((node) => {
                            const mod = moduleMap.get(node.interactionModuleId);
                            const isEntry = flow.entryNodeId === node._id;
                            return (
                              <tr
                                key={node._id}
                                className="border-t border-border"
                              >
                                <td className="px-3 py-2">
                                  <div className="flex items-center gap-2">
                                    <span>{node.label || "(No label)"}</span>
                                    {isEntry && (
                                      <Badge
                                        variant="outline"
                                        className="text-[10px] px-1"
                                      >
                                        Entry
                                      </Badge>
                                    )}
                                  </div>
                                </td>
                                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                                  {mod?.name || "(Unknown)"}
                                </td>
                                <td className="px-3 py-2 text-xs text-muted-foreground">
                                  {node.positionX !== undefined &&
                                  node.positionY !== undefined
                                    ? `(${node.positionX}, ${node.positionY})`
                                    : "—"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Edges Tab */}
              {detailTab === "edges" && (
                <div className="space-y-4">
                  {edges.length === 0 ? (
                    <div className="p-4 bg-muted/30 border border-border rounded-lg">
                      <div className="flex flex-col items-center text-center py-4">
                        <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center mb-3">
                          <GitBranch className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <p className="text-sm font-medium text-foreground mb-1">
                          This flow has no edges yet
                        </p>
                        <p className="text-xs text-muted-foreground mb-4">
                          Add edges to connect steps and define the flow.
                        </p>
                        {onAddEdge && (
                          <Button onClick={onAddEdge} size="sm">
                            <Plus className="h-3 w-3 mr-2" />
                            Add edge
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Header with Add Edge button */}
                      {onAddEdge && (
                        <div className="flex justify-end">
                          <Button
                            onClick={onAddEdge}
                            size="sm"
                            variant="outline"
                          >
                            <Plus className="h-3 w-3 mr-2" />
                            Add edge
                          </Button>
                        </div>
                      )}
                      <div className="border border-border rounded-md overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">
                                From
                              </th>
                              <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">
                                To
                              </th>
                              <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">
                                Condition
                              </th>
                              <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">
                                Priority
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {edges.map((edge) => (
                              <tr
                                key={edge._id}
                                className="border-t border-border"
                              >
                                <td className="px-3 py-2">
                                  {getNodeLabel(edge.fromNodeId)}
                                </td>
                                <td className="px-3 py-2">
                                  {edge.toNodeId
                                    ? getNodeLabel(edge.toNodeId)
                                    : "(End)"}
                                </td>
                                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                                  {edge.conditionKind || "—"}
                                </td>
                                <td className="px-3 py-2 text-xs text-muted-foreground">
                                  {edge.priority}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Drivers Tab */}
              {detailTab === "drivers" && (
                <div className="space-y-2">
                  {drivers.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                      No drivers in this flow.
                    </p>
                  ) : (
                    <div className="border border-border rounded-md overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">
                              Type
                            </th>
                            <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">
                              Name
                            </th>
                            <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">
                              Edge
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {drivers.map((driver) => {
                            // Find the edge this driver belongs to
                            const edge = edges.find(
                              (e) => e._id === driver.edgeId,
                            );
                            const edgeLabel = edge
                              ? `${getNodeLabel(edge.fromNodeId)} → ${edge.toNodeId ? getNodeLabel(edge.toNodeId) : "(End)"}`
                              : "(Unknown edge)";
                            return (
                              <tr
                                key={driver._id}
                                className="border-t border-border"
                              >
                                <td className="px-3 py-2">
                                  <Badge variant="outline" className="text-xs">
                                    {driver.type}
                                  </Badge>
                                </td>
                                <td className="px-3 py-2">
                                  {driver.name || "(No name)"}
                                </td>
                                <td className="px-3 py-2 text-xs text-muted-foreground">
                                  {edgeLabel}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </div>
    </ResizablePanel>
  );
}
